import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Copy, KeyRound, Link2, Pencil, Plus, RefreshCw, School, ScanLine, Trash2 } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import type { ExternalSchool, ExternalSchoolLevel, ExternalSchoolSummary } from '../../types/external-school';
import type { PracticeExam } from '../../types/practice-exam';
import OpticalUploadModal from './components/OpticalUploadModal';
import LgsOpticalUploadModal from './components/LgsOpticalUploadModal';

interface FormState {
    name: string;
    city: string;
    district: string;
    level: ExternalSchoolLevel;
    opticalCodes: string;
    partnerDepartmentId: string;
    linkEnabled: boolean;
    linkExpiresAt: string;
    studentDepartmentId: string;
}

const EMPTY_FORM: FormState = { name: '', city: '', district: '', level: 'ortaokul', opticalCodes: '', partnerDepartmentId: '', linkEnabled: true, linkExpiresAt: '', studentDepartmentId: '' };

const linkUrl = (school: ExternalSchool, admin = false) => `${window.location.origin}${school.linkPath}${admin ? '/okul' : ''}`;

const linkState = (school: ExternalSchool) => {
    if (!school.linkEnabled) return { label: 'Kapalı', className: 'bg-gray-100 text-gray-600' };
    if (school.linkExpiresAt && new Date(school.linkExpiresAt).getTime() <= Date.now()) return { label: 'Süresi doldu', className: 'bg-red-50 text-red-700' };
    return {
        label: school.linkExpiresAt ? `Açık · ${new Date(school.linkExpiresAt).toLocaleDateString('tr-TR')} tarihine kadar` : 'Açık',
        className: 'bg-emerald-50 text-emerald-700',
    };
};

/**
 * Kurum dışı denemelere öğrenci gönderen okullar. Her okulun tek bir karne bağlantısı var: öğrenci T.C. + okul
 * numarasıyla karnesini, okul yöneticisi şifreyle toplu listeyi görür.
 */
export default function ExternalSchools() {
    const { showAlert, showConfirm, showToast } = useModal();
    const [schools, setSchools] = useState<ExternalSchool[]>([]);
    const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<ExternalSchool | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [password, setPassword] = useState<{ school: ExternalSchool; value: string } | null>(null);
    // Okul kartı: girdiği denemeler (açılınca yüklenir) ve okul adına optik yükleme
    const [summaries, setSummaries] = useState<Record<string, ExternalSchoolSummary | 'loading'>>({});
    const [uploadSchool, setUploadSchool] = useState<ExternalSchool | null>(null);
    const [uploadExams, setUploadExams] = useState<PracticeExam[]>([]);
    const [uploadExam, setUploadExam] = useState<PracticeExam | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setSchools(await ApiService.getExternalSchools());
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error?.message || 'Kurumlar alınamadı', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        ApiService.getAllDepartments()
            .then((res: any) => {
                const rows = Array.isArray(res) ? res : (res?.data || res?.rows || res?.results || []);
                setDepartments(rows.map((row: any) => ({ id: row.id, name: row.name })));
            })
            .catch(() => setDepartments([]));
    }, []);

    const openForm = (school: ExternalSchool | null) => {
        setEditing(school);
        setForm(school ? {
            name: school.name,
            city: school.city || '',
            district: school.district || '',
            level: school.level,
            opticalCodes: (school.opticalCodes || []).join(', '),
            partnerDepartmentId: school.partnerDepartmentId || '',
            linkEnabled: school.linkEnabled,
            linkExpiresAt: school.linkExpiresAt ? String(school.linkExpiresAt).slice(0, 10) : '',
            studentDepartmentId: school.studentDepartmentId || '',
        } : EMPTY_FORM);
        setShowForm(true);
    };

    const save = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                city: form.city.trim() || null,
                district: form.district.trim() || null,
                level: form.level,
                opticalCodes: form.opticalCodes,
                partnerDepartmentId: form.partnerDepartmentId || null,
                linkEnabled: form.linkEnabled,
                // Bitiş günü dahil açık kalsın
                linkExpiresAt: form.linkExpiresAt ? new Date(`${form.linkExpiresAt}T23:59:59`).toISOString() : null,
                studentDepartmentId: form.studentDepartmentId || null,
            };
            if (editing) await ApiService.updateExternalSchool(editing.id, payload);
            else await ApiService.createExternalSchool(payload);
            setShowForm(false);
            await load();
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error?.message || 'Kurum kaydedilemedi', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const toggleSummary = async (school: ExternalSchool) => {
        if (summaries[school.id]) {
            setSummaries((prev) => { const next = { ...prev }; delete next[school.id]; return next; });
            return;
        }
        setSummaries((prev) => ({ ...prev, [school.id]: 'loading' }));
        try {
            const summary = await ApiService.getExternalSchoolSummary(school.id);
            setSummaries((prev) => ({ ...prev, [school.id]: summary }));
        } catch (error: any) {
            setSummaries((prev) => { const next = { ...prev }; delete next[school.id]; return next; });
            showAlert({ title: 'Hata', message: error?.message || 'Özet alınamadı', type: 'error' });
        }
    };

    // Okul adına yükleme: kademeye uyan Akademi sistemi denemeleri (lise → YKS, ortaokul → LGS)
    const openUpload = async (school: ExternalSchool) => {
        setUploadSchool(school);
        setUploadExam(null);
        try {
            const res = await ApiService.getPracticeExams({ systemType: 'internal', levelType: school.level === 'lise' ? 'yks' : 'lgs', limit: 100 });
            setUploadExams(res?.data || []);
        } catch (error: any) {
            setUploadExams([]);
            showAlert({ title: 'Hata', message: error?.message || 'Denemeler alınamadı', type: 'error' });
        }
    };
    const closeUpload = () => {
        const school = uploadSchool;
        setUploadSchool(null);
        setUploadExam(null);
        load();
        if (school && summaries[school.id]) {
            ApiService.getExternalSchoolSummary(school.id).then((summary) => setSummaries((prev) => ({ ...prev, [school.id]: summary }))).catch(() => undefined);
        }
    };

    const copy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`${label} kopyalandı`, 'success');
        } catch (_) {
            showAlert({ title: label, message: text, type: 'info' });
        }
    };

    const rotate = async (school: ExternalSchool) => {
        const ok = await showConfirm({
            title: 'Bağlantıyı yenile',
            message: `${school.name} için yeni bağlantı üretilsin mi? Eski bağlantı hemen çalışmaz olur; yenisini okula tekrar göndermeniz gerekir.`,
            confirmText: 'Yenile',
            confirmButtonVariant: 'warning',
        });
        if (!ok) return;
        try {
            await ApiService.rotateExternalSchoolLink(school.id);
            await load();
            showToast('Yeni bağlantı üretildi', 'success');
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error?.message || 'Bağlantı yenilenemedi', type: 'error' });
        }
    };

    const makePassword = async (school: ExternalSchool) => {
        if (school.hasAdminPassword) {
            const ok = await showConfirm({
                title: 'Yeni yönetici şifresi',
                message: 'Yeni şifre üretilince eski şifre çalışmaz. Devam edilsin mi?',
                confirmText: 'Yeni şifre üret',
                confirmButtonVariant: 'warning',
            });
            if (!ok) return;
        }
        try {
            const res = await ApiService.setExternalSchoolAdminPassword(school.id);
            setPassword({ school: res.school, value: res.password });
            await load();
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error?.message || 'Şifre üretilemedi', type: 'error' });
        }
    };

    const remove = async (school: ExternalSchool) => {
        const ok = await showConfirm({
            title: 'Kurumu sil',
            message: `${school.name} silinsin mi? Bağlantısı çalışmaz olur; yüklenmiş sonuçlar silinmez.`,
            confirmText: 'Sil',
            confirmButtonVariant: 'danger',
        });
        if (!ok) return;
        try {
            await ApiService.deleteExternalSchool(school.id);
            await load();
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error?.message || 'Kurum silinemedi', type: 'error' });
        }
    };

    const departmentName = (id?: string | null) => departments.find((row) => row.id === id)?.name || null;
    const input = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500';

    return (
        <div className="flex h-full flex-col space-y-4 overflow-y-auto p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <p className="max-w-3xl text-sm text-gray-600">
                    Kurum dışı denemelere öğrenci gönderen okullar. Her okula tek bir karne bağlantısı verilir: öğrenci T.C. kimlik ve okul
                    numarasıyla kendi karnesini, okul yöneticisi şifreyle okulun toplu listesini görür. Optikteki kurum kodları yüklemede
                    satırların okula dağıtılmasında kullanılır.
                </p>
                <button
                    onClick={() => openForm(null)}
                    className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                    <Plus size={16} /> Kurum ekle
                </button>
            </div>

            {loading ? (
                <div className="py-10 text-center text-sm text-gray-500">Yükleniyor…</div>
            ) : schools.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                    <School className="mx-auto mb-2 text-gray-400" size={28} />
                    Henüz dış kurum yok.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {schools.map((school) => {
                        const state = linkState(school);
                        const partner = departmentName(school.partnerDepartmentId);
                        return (
                            <div key={school.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="truncate text-base font-semibold text-gray-900">{school.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {[school.district, school.city].filter(Boolean).join(' / ') || '—'} · {school.level === 'lise' ? 'Lise' : 'Ortaokul'}
                                            {partner ? ` · Kırtasiye: ${partner}` : ''}
                                        </div>
                                    </div>
                                    <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${state.className}`}>{state.label}</span>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">{school.resultCount} sonuç</span>
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                                        Kurum kodu: {school.opticalCodes?.length ? school.opticalCodes.join(', ') : 'yok'}
                                    </span>
                                    <span className={`rounded-full px-2.5 py-1 ${school.hasAdminPassword ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
                                        {school.hasAdminPassword ? 'Yönetici şifresi var' : 'Yönetici şifresi yok'}
                                    </span>
                                    <span className={`rounded-full px-2.5 py-1 ${school.studentDepartmentName ? 'bg-sky-50 text-sky-800' : 'bg-gray-100 text-gray-500'}`}>
                                        Kayıtlı öğrenciler: {school.studentDepartmentName || 'bölüm seçilmedi'}
                                    </span>
                                </div>

                                {summaries[school.id] && (
                                    <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-2.5 text-xs">
                                        {summaries[school.id] === 'loading' ? (
                                            <div className="text-gray-500">Yükleniyor…</div>
                                        ) : (summaries[school.id] as ExternalSchoolSummary).exams.length === 0 ? (
                                            <div className="text-gray-500">Bu okulun henüz deneme sonucu yok.</div>
                                        ) : (
                                            <>
                                                <ul className="space-y-1">
                                                    {(summaries[school.id] as ExternalSchoolSummary).exams.map((row) => (
                                                        <li key={row.examId} className="flex flex-wrap items-center justify-between gap-2">
                                                            <span className="min-w-0 truncate font-medium text-gray-800">{row.name}</span>
                                                            <span className="text-gray-600">kayıtlı {row.registered} · kurum dışı {row.external}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <p className="mt-2 text-gray-500">Karneler: Karneler sekmesinde denemeyi açıp okul süzgecinden "{school.name}"u seçin.</p>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button onClick={() => openUpload(school)} className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
                                        <ScanLine size={14} /> Sonuç yükle
                                    </button>
                                    <button onClick={() => toggleSummary(school)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <BarChart3 size={14} /> {summaries[school.id] ? 'Denemeleri gizle' : 'Denemeler'}
                                    </button>
                                    <button onClick={() => copy(linkUrl(school), 'Öğrenci bağlantısı')} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <Link2 size={14} /> Öğrenci bağlantısı
                                    </button>
                                    <button onClick={() => copy(linkUrl(school, true), 'Yönetici bağlantısı')} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <Copy size={14} /> Yönetici bağlantısı
                                    </button>
                                    <button onClick={() => makePassword(school)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <KeyRound size={14} /> {school.hasAdminPassword ? 'Yeni şifre' : 'Şifre üret'}
                                    </button>
                                    <button onClick={() => rotate(school)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <RefreshCw size={14} /> Bağlantıyı yenile
                                    </button>
                                    <button onClick={() => openForm(school)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                                        <Pencil size={14} /> Düzenle
                                    </button>
                                    <button onClick={() => remove(school)} className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-xs text-red-700 hover:bg-red-50">
                                        <Trash2 size={14} /> Sil
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal isOpen={showForm} onClose={() => !saving && setShowForm(false)} title={editing ? 'Kurumu düzenle' : 'Kurum ekle'} size="md">
                <div className="space-y-3">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Okul adı <span className="text-red-500">*</span></label>
                        <input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn. Atatürk Ortaokulu" />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">İl</label>
                            <input className={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">İlçe</label>
                            <input className={input} value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Kademe</label>
                            <select className={input} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value as ExternalSchoolLevel })}>
                                <option value="ortaokul">Ortaokul</option>
                                <option value="lise">Lise</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Kırtasiye (departman)</label>
                            <select className={input} value={form.partnerDepartmentId} onChange={(e) => setForm({ ...form, partnerDepartmentId: e.target.value })}>
                                <option value="">Yok</option>
                                {departments.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Sistemde kayıtlı öğrencilerinin bölümü (isteğe bağlı)</label>
                        <select className={input} value={form.studentDepartmentId} onChange={(e) => setForm({ ...form, studentDepartmentId: e.target.value })}>
                            <option value="">Yok</option>
                            {departments.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                            Örn. "Mimar Sinan Lisesi 12. Sınıf". Okula sonuç yüklerken kayıtlı öğrenciler bu bölümde aranır; sistemden cevap giren
                            öğrenciler de okulun listesinde görünür.
                        </p>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Optikteki kurum kodları</label>
                        <input className={input} value={form.opticalCodes} onChange={(e) => setForm({ ...form, opticalCodes: e.target.value })} placeholder="Örn. 123456, 654321" />
                        <p className="mt-1 text-xs text-gray-500">Virgülle ayırın. "Kurum koduna göre dağıt" yüklemesinde satırlar bu kodlarla okula yazılır.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={form.linkEnabled} onChange={(e) => setForm({ ...form, linkEnabled: e.target.checked })} />
                            Karne bağlantısı açık
                        </label>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Bağlantı bitiş tarihi (isteğe bağlı)</label>
                            <input type="date" className={input} value={form.linkExpiresAt} onChange={(e) => setForm({ ...form, linkExpiresAt: e.target.value })} />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
                        <button onClick={() => setShowForm(false)} disabled={saving} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">İptal</button>
                        <button onClick={save} disabled={saving || !form.name.trim()} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                            {saving ? 'Kaydediliyor…' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={Boolean(uploadSchool) && !uploadExam} onClose={closeUpload} title={uploadSchool ? `${uploadSchool.name} — sonuç yükle` : 'Sonuç yükle'} size="md">
                {uploadSchool && (
                    <div className="space-y-2 text-sm">
                        <p className="text-gray-600">Sonuçlarını yükleyeceğiniz denemeyi seçin.</p>
                        {uploadExams.length === 0 ? (
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-gray-500">Uygun deneme yok ({uploadSchool.level === 'lise' ? 'YKS' : 'LGS'} Akademi sistemi denemesi gerekir).</div>
                        ) : (
                            <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                                {uploadExams.map((exam) => (
                                    <li key={exam.id}>
                                        <button onClick={() => setUploadExam(exam)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-blue-50">
                                            <span className="min-w-0 truncate font-medium text-gray-800">{exam.name}</span>
                                            <span className="flex-shrink-0 text-xs text-gray-500">{exam.date ? new Date(exam.date).toLocaleDateString('tr-TR') : ''}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </Modal>

            {uploadSchool && uploadExam && uploadExam.levelType !== 'lgs' && (
                <OpticalUploadModal exam={uploadExam} isOpen onClose={closeUpload} onImported={() => undefined} presetSchoolId={uploadSchool.id} />
            )}
            {uploadSchool && uploadExam && uploadExam.levelType === 'lgs' && (
                <LgsOpticalUploadModal
                    exam={uploadExam}
                    isOpen
                    onClose={closeUpload}
                    onImported={() => undefined}
                    schoolOptions={[{ id: uploadSchool.id, name: uploadSchool.name, level: uploadSchool.level, opticalCodes: uploadSchool.opticalCodes || [] }]}
                />
            )}

            <Modal isOpen={Boolean(password)} onClose={() => setPassword(null)} title="Okul yöneticisi şifresi" size="sm">
                {password && (
                    <div className="space-y-3 text-sm">
                        <p className="text-gray-600">
                            <b>{password.school.name}</b> yöneticisinin şifresi aşağıda. Bu şifre <b>yalnız şimdi</b> gösterilir; kaybolursa yenisini üretin.
                        </p>
                        <div className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 font-mono text-base tracking-wider text-gray-900">
                            <span className="flex-1 select-all">{password.value}</span>
                            <button onClick={() => copy(password.value, 'Şifre')} className="text-gray-600 hover:text-gray-900" aria-label="Şifreyi kopyala"><Copy size={16} /></button>
                        </div>
                        <p className="text-xs text-gray-500">Yönetici bağlantısı: <span className="break-all">{linkUrl(password.school, true)}</span></p>
                        <div className="flex justify-end">
                            <button onClick={() => setPassword(null)} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Tamam</button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
